import { authStore } from "@/core/store/auth.store";
import { notificationsStore } from "@/core/store/notifications.store";
import { logout, linkEmailPassword } from "@/core/services/auth.service";
import { auth } from "@/core/config/firebase";
import type { UserProfile, NotificationBase } from "@/core/types";
import headerProfileHtml from "./header-profile.html?raw";
// Importar el HTML de la campanita flotante (integrada)
import notificationBellHtml from "./floating-bell.html?raw";
import { FirebaseError } from "firebase/app";

/**
 * @file src/components/header-profile/header-profile.ts
 * @description Lógica para el componente del menú de perfil y la campanita de notificaciones.
 */

export function initHeaderProfile(): void {
  const containerId = "app-header-profile-container";
  if (document.getElementById(containerId)) {
    return;
  }

  const container = document.createElement("div");
  container.id = containerId;
  container.className = "app-header-profile-trigger relative";
  container.style.display = "block"; // Asegurar que sea visible
  document.body.appendChild(container);

  try {
    container.innerHTML = headerProfileHtml;

    // Inicializar la campanita flotante
    initFloatingNotificationBell();

    setupProfileEventListeners();

    // Suscribirse a los cambios del estado de autenticación
    authStore.subscribe((state) => {
      updateProfileUI(state.user, state.firebaseUser);
      updateLinkPasswordButton();
    });

    // Verificar proveedores cuando cambia el usuario autenticado
    auth.onAuthStateChanged(() => {
      updateLinkPasswordButton();
    });

    // Suscribirse a los cambios de notificaciones para la campanita
    notificationsStore.subscribe((state) => {
      updateHeaderNotificationBell(state.unreadCount);

      // Mostrar campanita flotante si hay una notificación nueva
      if (state.isFloatingVisible && state.currentFloatingNotification) {
        showFloatingNotification(state.currentFloatingNotification);
      }

      // Ocultar campanita flotante si no hay notificación visible
      if (!state.isFloatingVisible) {
        hideFloatingNotificationUI();
      }
    });

    // Cargar datos iniciales del store
    const initialState = authStore.getState();
    updateProfileUI(initialState.user, initialState.firebaseUser);
    updateHeaderNotificationBell(notificationsStore.getState().unreadCount);
    updateLinkPasswordButton();

    // Configurar event listeners para vincular contraseña
    setupLinkPasswordListeners();
  } catch (error) {
    console.error("Error al inicializar el menú de perfil:", error);
    container.innerHTML = '<p class="text-red-500">Error</p>';
  }
}

/**
 * Configura los event listeners para la interactividad del menú y la campanita.
 */
function setupProfileEventListeners(): void {
  const profileTrigger = document.getElementById("profileDropdownTrigger");
  const menu = document.getElementById("profileDropdownMenu");
  const logoutBtn = document.getElementById("logoutButtonDropdown");
  const openNotificationsBtn = document.getElementById(
    "openNotificationsModal",
  );
  const notificationBell = document.getElementById("headerNotificationBell");

  if (!profileTrigger || !menu || !logoutBtn || !notificationBell) {
    console.warn("Elementos del DOM para el menú de perfil no encontrados.");
    return;
  }

  // Abrir/Cerrar menú de perfil
  profileTrigger.addEventListener("click", (event: MouseEvent) => {
    event.stopPropagation();
    menu.classList.toggle("show");
  });

  // Cerrar menú si se hace clic fuera
  document.addEventListener("click", (event: MouseEvent) => {
    const target = event.target as Node;
    // Asegurarse de que el clic no sea dentro del trigger ni del menú mismo
    if (
      menu.classList.contains("show") &&
      !profileTrigger.contains(target) &&
      !menu.contains(target)
    ) {
      menu.classList.remove("show");
    }
  });

  // Acción de cerrar sesión
  logoutBtn.addEventListener("click", async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      alert("Hubo un problema al cerrar la sesión.");
    }
  });

  // Función para abrir el modal grande de notificaciones (desde menú desplegable)
  const openFullModalAction = () => {
    menu.classList.remove("show"); // Siempre cierra el menú de perfil
    const event = new CustomEvent("open-notifications-modal");
    document.dispatchEvent(event);
    console.log("🔔 Evento open-notifications-modal despachado.");
  };

  // Función para mostrar las notificaciones flotantes (desde campanita)
  const showFloatingNotifications = () => {
    menu.classList.remove("show"); // Cierra el menú si está abierto

    // Obtener notificaciones no leídas del store
    const notificationsState = notificationsStore.getState();
    const unreadNotifications = notificationsState.notifications.filter(
      (n) => !n.leida,
    );

    if (unreadNotifications.length > 0) {
      // Mostrar la notificación más reciente como flotante
      const latestNotification = unreadNotifications[0];
      notificationsStore.showFloatingNotification(latestNotification);
      console.log(
        "🔔 Mostrando notificación flotante:",
        latestNotification.titulo,
      );
    } else {
      console.log("🔔 No hay notificaciones no leídas para mostrar");
    }
  };

  // Click en la campanita principal - mostrar notificaciones flotantes
  notificationBell.addEventListener("click", showFloatingNotifications);

  // Click en el botón de notificaciones dentro del menú desplegable - abrir modal grande
  openNotificationsBtn?.addEventListener("click", openFullModalAction);
}

/**
 * Configura los event listeners para vincular contraseña
 */
function setupLinkPasswordListeners(): void {
  const linkPasswordBtn = document.getElementById("linkPasswordButton");
  const linkPasswordModal = document.getElementById("linkPasswordModal");
  const closeModalBtn = document.getElementById("closeLinkPasswordModal");
  const cancelBtn = document.getElementById("cancelLinkPassword");
  const linkPasswordForm = document.getElementById(
    "linkPasswordForm",
  ) as HTMLFormElement;
  const messageDiv = document.getElementById("linkPasswordMessage");

  if (!linkPasswordBtn || !linkPasswordModal || !linkPasswordForm) {
    return;
  }

  // Abrir modal
  linkPasswordBtn.addEventListener("click", () => {
    const user = auth.currentUser;
    if (user && user.email) {
      const emailInput = document.getElementById(
        "linkPasswordEmail",
      ) as HTMLInputElement;
      if (emailInput) {
        emailInput.value = user.email;
      }
    }
    linkPasswordModal.classList.remove("hidden");
    linkPasswordModal.classList.add("flex");
  });

  // Cerrar modal
  const closeModal = () => {
    linkPasswordModal.classList.add("hidden");
    linkPasswordModal.classList.remove("flex");
    if (messageDiv) {
      messageDiv.classList.add("hidden");
      messageDiv.textContent = "";
    }
    linkPasswordForm.reset();
  };

  closeModalBtn?.addEventListener("click", closeModal);
  cancelBtn?.addEventListener("click", closeModal);

  // Cerrar al hacer clic fuera del modal
  linkPasswordModal.addEventListener("click", (e: MouseEvent) => {
    if (e.target === linkPasswordModal) {
      closeModal();
    }
  });

  // Cerrar con tecla Escape
  document.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && !linkPasswordModal.classList.contains("hidden")) {
      closeModal();
    }
  });

  // Manejar envío del formulario
  linkPasswordForm.addEventListener("submit", async (e: SubmitEvent) => {
    e.preventDefault();

    const emailInput = document.getElementById(
      "linkPasswordEmail",
    ) as HTMLInputElement;
    const passwordInput = document.getElementById(
      "linkPasswordPassword",
    ) as HTMLInputElement;
    const submitBtn = document.getElementById(
      "submitLinkPassword",
    ) as HTMLButtonElement;

    if (!emailInput || !passwordInput || !messageDiv) {
      return;
    }

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    if (!email || !password) {
      showLinkPasswordMessage("Por favor completa todos los campos.", "error");
      return;
    }

    if (password.length < 6) {
      showLinkPasswordMessage(
        "La contraseña debe tener al menos 6 caracteres.",
        "error",
      );
      return;
    }

    // Deshabilitar botón durante el proceso
    submitBtn.disabled = true;
    submitBtn.textContent = "Vinculando...";

    try {
      await linkEmailPassword(email, password);
      showLinkPasswordMessage(
        "¡Contraseña vinculada exitosamente! Ahora puedes iniciar sesión con correo y contraseña.",
        "success",
      );

      // Actualizar el botón después de vincular
      setTimeout(() => {
        updateLinkPasswordButton();
        setTimeout(() => {
          closeModal();
        }, 2000);
      }, 1000);
    } catch (error) {
      console.error("Error al vincular contraseña:", error);
      let errorMessage = "Error al vincular la contraseña. Intenta de nuevo.";

      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/email-already-in-use":
            errorMessage = "Este correo ya está asociado con otra cuenta.";
            break;
          case "auth/weak-password":
            errorMessage = "La contraseña es demasiado débil.";
            break;
          case "auth/invalid-email":
            errorMessage = "El formato del correo electrónico no es válido.";
            break;
          case "auth/credential-already-in-use":
            errorMessage = "Esta credencial ya está vinculada a otra cuenta.";
            break;
          default:
            errorMessage = `Error: ${error.message}`;
        }
      }

      showLinkPasswordMessage(errorMessage, "error");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = "Vincular";
    }
  });
}

/**
 * Muestra un mensaje en el modal de vincular contraseña
 */
function showLinkPasswordMessage(
  message: string,
  type: "success" | "error",
): void {
  const messageDiv = document.getElementById("linkPasswordMessage");
  if (!messageDiv) return;

  messageDiv.textContent = message;
  messageDiv.classList.remove("hidden");

  if (type === "success") {
    messageDiv.className =
      "text-sm py-2 px-3 rounded bg-green-100 text-green-700 border border-green-300";
  } else {
    messageDiv.className =
      "text-sm py-2 px-3 rounded bg-red-100 text-red-700 border border-red-300";
  }
}

/**
 * Actualiza la visibilidad del botón de vincular contraseña según los proveedores del usuario
 */
function updateLinkPasswordButton(): void {
  const linkPasswordBtn = document.getElementById("linkPasswordButton");
  if (!linkPasswordBtn) return;

  const user = auth.currentUser;
  if (!user) {
    linkPasswordBtn.classList.add("hidden");
    return;
  }

  // Verificar qué proveedores tiene vinculados
  const providers = user.providerData.map((provider) => provider.providerId);
  const hasPasswordProvider = providers.includes("password");
  const hasGoogleProvider = providers.includes("google.com");

  // Mostrar el botón solo si tiene Google pero no tiene password
  if (hasGoogleProvider && !hasPasswordProvider) {
    linkPasswordBtn.classList.remove("hidden");
  } else {
    linkPasswordBtn.classList.add("hidden");
  }
}

/**
 * Actualiza la UI del perfil con los datos del usuario.
 * @param user El objeto de perfil de usuario o null.
 * @param firebaseUser El objeto de usuario de Firebase Auth o null.
 */
function updateProfileUI(
  user: UserProfile | null,
  firebaseUser: import("firebase/auth").User | null,
): void {
  const headerAvatar = document.getElementById(
    "headerProfileAvatar",
  ) as HTMLImageElement;
  const headerInitials = document.getElementById(
    "headerProfileInitials",
  ) as HTMLDivElement;
  const dropdownPic = document.getElementById(
    "dropdownProfilePic",
  ) as HTMLImageElement;
  const dropdownName = document.getElementById(
    "dropdownUserName",
  ) as HTMLSpanElement;
  const dropdownEmail = document.getElementById(
    "dropdownUserEmail",
  ) as HTMLSpanElement;

  if (
    !headerAvatar ||
    !headerInitials ||
    !dropdownPic ||
    !dropdownName ||
    !dropdownEmail
  ) {
    return;
  }

  if (user) {
    dropdownName.textContent = `${user.nombre} ${user.apellido || ""}`.trim();
    dropdownEmail.textContent = user.email;

    // Priorizar la foto de Firebase Auth si está disponible (más actualizada, especialmente cuando se vincula con Google)
    // Si el usuario tiene Google vinculado, obtener la foto del provider de Google
    let photoURL = firebaseUser?.photoURL || user.photoURL;

    // Si no hay foto pero el usuario tiene Google vinculado, buscar en los providers
    if (!photoURL && firebaseUser) {
      const googleProvider = firebaseUser.providerData.find(
        (provider) => provider.providerId === "google.com",
      );
      if (googleProvider?.photoURL) {
        photoURL = googleProvider.photoURL;
      }
    }

    if (photoURL && photoURL.trim() !== "") {
      // Asegurar que solo se muestre el avatar, no las iniciales
      headerAvatar.src = photoURL;
      dropdownPic.src = photoURL;
      headerAvatar.classList.remove("hidden");
      headerAvatar.style.display = ""; // Remover estilo inline si existe
      headerInitials.classList.add("hidden");
      headerInitials.style.display = "none"; // Asegurar que esté oculto
    } else {
      // Asegurar que solo se muestren las iniciales, no el avatar
      const initials = `${user.nombre.charAt(0)}${
        user.apellido ? user.apellido.charAt(0) : ""
      }`.toUpperCase();
      headerInitials.textContent = initials;
      dropdownPic.src = `https://ui-avatars.com/api/?name=${initials}&background=EDE9FE&color=7C3AED`;
      headerAvatar.classList.add("hidden");
      headerAvatar.style.display = "none"; // Asegurar que esté oculto
      headerInitials.classList.remove("hidden");
      headerInitials.style.display = "flex"; // Asegurar que esté visible
    }
  } else {
    // Estado por defecto si no hay usuario
    dropdownName.textContent = "Sin usuario";
    dropdownEmail.textContent = "Inicia sesión";
    headerInitials.textContent = "QP";
    dropdownPic.src = `https://ui-avatars.com/api/?name=QP&background=EDE9FE&color=7C3AED`;
    headerAvatar.classList.add("hidden");
    headerAvatar.style.display = "none"; // Asegurar que esté oculto
    headerInitials.classList.remove("hidden");
    headerInitials.style.display = ""; // Usar el estilo del CSS (flex por defecto)
  }
}

/**
 * Actualiza la campanita de notificaciones en el header.
 * @param unreadCount Número de notificaciones no leídas.
 */
function updateHeaderNotificationBell(unreadCount: number): void {
  const bellContainer = document.getElementById("headerNotificationBell");
  const badge = document.getElementById("headerNotificationBadge");

  if (!bellContainer || !badge) return;

  if (unreadCount > 0) {
    // Mostrar campanita y badge
    bellContainer.classList.remove("hidden");
    bellContainer.classList.add("flex"); // Asegurar que se muestre como flex
    badge.textContent = unreadCount > 9 ? "9+" : String(unreadCount);
    badge.classList.remove("hidden");
    badge.classList.add("visible", "flex"); // Activa la animación

    // Añade animación de "ring" solo si el contador cambia de 0 a >0
    if (!bellContainer.classList.contains("has-notifications")) {
      bellContainer.classList.add("has-notifications");
      // Quitar la clase después de la animación para que pueda repetirse en el futuro
      setTimeout(
        () => bellContainer.classList.remove("has-notifications"),
        800,
      );
    }
  } else {
    // Ocultar campanita completamente cuando no hay notificaciones
    bellContainer.classList.add("hidden");
    bellContainer.classList.remove("flex");
    badge.classList.add("hidden");
    badge.classList.remove("visible", "flex");
  }
}

/**
 * Inicializa la campanita flotante de notificaciones
 */
function initFloatingNotificationBell(): void {
  // Inyectar el HTML de la campanita flotante en el body
  const floatingContainer = document.createElement("div");
  floatingContainer.id = "notification-bell-floating-container";
  floatingContainer.innerHTML = notificationBellHtml;
  document.body.appendChild(floatingContainer);

  // Configurar event listeners para la campanita flotante
  setupFloatingBellEventListeners();

  console.log("🔔 Campanita flotante inicializada desde header-profile");
}

/**
 * Configura los event listeners de la campanita flotante
 */
function setupFloatingBellEventListeners(): void {
  const closeBellFloating = document.getElementById("closeBellFloating");
  const completeTaskFromBell = document.getElementById("completeTaskFromBell");
  const viewNotificationDetail = document.getElementById(
    "viewNotificationDetail",
  );

  // Cerrar campanita flotante
  closeBellFloating?.addEventListener("click", () => {
    hideFloatingNotification();
  });

  // Completar tarea desde la campanita
  completeTaskFromBell?.addEventListener("click", async () => {
    const notificationState = notificationsStore.getState();
    if (notificationState.currentFloatingNotification?.tareaId) {
      await handleCompleteTaskFromFloatingBell(
        notificationState.currentFloatingNotification.tareaId,
      );
    }
  });

  // Ver detalle de notificación
  viewNotificationDetail?.addEventListener("click", () => {
    const notificationState = notificationsStore.getState();
    if (notificationState.currentFloatingNotification) {
      handleViewNotificationDetailFromFloatingBell(
        notificationState.currentFloatingNotification,
      );
    }
  });

  // Auto-ocultar después de 10 segundos
  setTimeout(() => {
    const bellFloating = document.getElementById("notificationBellFloating");
    if (bellFloating && !bellFloating.classList.contains("hidden")) {
      hideFloatingNotification();
    }
  }, 10000);
}

/**
 * Muestra la campanita flotante con una notificación
 */
function showFloatingNotification(notification: NotificationBase): void {
  const bellFloating = document.getElementById("notificationBellFloating");
  const title = document.getElementById("notificationBellTitle");
  const message = document.getElementById("notificationBellMessage");
  const time = document.getElementById("notificationBellTime");
  const client = document
    .getElementById("notificationBellClient")
    ?.querySelector("span");
  const completeBtn = document.getElementById("completeTaskFromBell");

  if (!bellFloating || !title || !message || !time) return;

  // Llenar contenido
  title.textContent = notification.titulo;
  message.textContent = notification.mensaje;
  time.textContent = formatTimeAgo(notification.fechaCreacion);

  if (client && notification.clienteNombre) {
    client.textContent = notification.clienteNombre;
  }

  // Mostrar/ocultar botón de completar tarea
  if (completeBtn) {
    const isTareaNotification =
      notification.tipo === "tarea_recordatorio" ||
      notification.tipo === "tarea_vencida";
    const isTaskCompleted = notification.datos?.tareaCompletada === true;

    if (isTareaNotification && !isTaskCompleted) {
      completeBtn.classList.remove("hidden");
    } else {
      completeBtn.classList.add("hidden");
    }
  }

  // Mostrar campanita con transición suave
  bellFloating.classList.remove("hidden");
  bellFloating.style.opacity = "0";
  bellFloating.style.transform = "translateX(20px) scale(0.95)";

  // Aplicar transición suave
  setTimeout(() => {
    bellFloating.style.opacity = "1";
    bellFloating.style.transform = "translateX(0) scale(1)";
  }, 50);

  console.log("🔔 Mostrando campanita flotante:", notification.titulo);
}

/**
 * Oculta la campanita flotante
 */
function hideFloatingNotification(): void {
  const bellFloating = document.getElementById("notificationBellFloating");
  if (bellFloating) {
    // Transición suave al ocultar
    bellFloating.style.opacity = "0";
    bellFloating.style.transform = "translateX(20px) scale(0.95)";

    setTimeout(() => {
      bellFloating.classList.add("hidden");
      bellFloating.style.opacity = "";
      bellFloating.style.transform = "";
    }, 300);
  }

  // Marcar todas las notificaciones no leídas como leídas
  const notificationsState = notificationsStore.getState();
  const unreadNotifications = notificationsState.notifications.filter(
    (n) => !n.leida,
  );

  unreadNotifications.forEach((notification) => {
    // Importar dinámicamente el servicio de notificaciones
    import("@/core/services/notifications.service").then(
      ({ notificationsService }) => {
        notificationsService.markAsRead(notification.id);
      },
    );
  });

  // Ocultar la campanita flotante en el store
  notificationsStore.hideFloatingNotification();

  console.log(
    "🔔 Campanita flotante cerrada y notificaciones marcadas como leídas",
  );
}

/**
 * Oculta solo la UI de la campanita flotante (sin actualizar el store)
 */
function hideFloatingNotificationUI(): void {
  const bellFloating = document.getElementById("notificationBellFloating");
  if (bellFloating) {
    // Transición suave al ocultar (solo UI)
    bellFloating.style.opacity = "0";
    bellFloating.style.transform = "translateX(20px) scale(0.95)";

    setTimeout(() => {
      bellFloating.classList.add("hidden");
      bellFloating.style.opacity = "";
      bellFloating.style.transform = "";
    }, 300);
  }
}

/**
 * Maneja el completado de tarea desde la campanita flotante
 */
async function handleCompleteTaskFromFloatingBell(
  tareaId: string,
): Promise<void> {
  try {
    // Importar dinámicamente el servicio de notificaciones
    const { notificationsService } = await import(
      "@/core/services/notifications.service"
    );
    notificationsService.updateTaskCompletion(tareaId, true);

    // Ocultar el botón de completar en la campanita actual
    const completeBtn = document.getElementById("completeTaskFromBell");
    if (completeBtn) {
      completeBtn.classList.add("hidden");
    }

    console.log("✅ Tarea completada desde campanita flotante:", tareaId);
  } catch (error) {
    console.error("Error completando tarea desde campanita flotante:", error);
  }
}

/**
 * Maneja la visualización del detalle de notificación desde la campanita flotante
 */
function handleViewNotificationDetailFromFloatingBell(notification: NotificationBase): void {
  // Navegar a la vista del cliente si hay clienteId
  if (notification.clienteId) {
    window.location.href = `/vistacliente?id=${notification.clienteId}`;
  }

  // Ocultar la campanita
  hideFloatingNotification();
}

/**
 * Formatea el tiempo transcurrido desde una fecha
 */
function formatTimeAgo(date: Date | { toDate: () => Date }): string {
  const now = new Date();
  const notificationDate = date instanceof Date ? date : date.toDate();
  const diffMs = now.getTime() - notificationDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "Ahora mismo";
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 30) return `Hace ${diffDays}d`;

  return notificationDate.toLocaleDateString();
}
